// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Explorer Workspace
// Process tree sidebar + Ficha (colapsable) + Visor (expande)
// Adapted for AppShell: NO own header, NO navigation links
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, TIPOLOGIA_CONFIG, VERSION_STATUS_CONFIG } from '../../../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { fetchProcesos, fetchPendientes } from '../../../../shared/services/api';
import type {
  Proceso,
  DocumentoResumen,
  TipologiaProceso,
  EstadoDocumento,
} from '../../../../../types';
import { ProcessSheetPanel } from '../../components/ProcessSheetPanel';
import { DocumentPreviewPanel } from '../components/DocumentPreviewPanel';
import type { PanelState } from '../components/DocumentPreviewPanel';
import { AppHeader } from '../../../../shared/components/AppHeader';
import { ExplorerPage } from './ExplorerPage';
import { exportFichaToPdf } from '../../../../../lib/exportPdf';
import FlowEditor from '../../flow/editor/FlowEditor';

// ─── Types ───
interface GroupedProcess {
  tipologia: TipologiaProceso;
  groups: { name: string; procesos: Proceso[] }[];
}

// ─── Constants ───
const SIDEBAR_W = 260;
const FICHA_COLLAPSED_W = 48;

const hdrBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: C.textSec,
  backgroundColor: 'transparent',
  border: `1px solid ${C.border}`,
  borderRadius: 5,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s',
};

const hdrBtnPrimary: React.CSSProperties = {
  ...hdrBtn,
  color: '#fff',
  backgroundColor: C.azul2,
  border: 'none',
  fontWeight: 600,
};

const wfBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '3px 10px',
  fontSize: 10,
  fontWeight: 600,
  fontFamily: 'inherit',
  borderRadius: 4,
  border: '1px solid',
  background: 'white',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'all 0.15s ease',
};

// ═══════════════════════════════════════════════════════════════
// PROCESS TREE SIDEBAR — Only the process tree, no navigation
// ═══════════════════════════════════════════════════════════════
function ProcessTreeSidebar({
  selectedCodigo,
  onSelectProceso,
  onGoExplorer,
  onNavigateDoc,
}: {
  selectedCodigo: string | null;
  onSelectProceso: (p: Proceso) => void;
  onGoExplorer: () => void;
  onNavigateDoc: (codigo: string) => void;
}) {
  const { t } = useI18n();
  const [procesos, setProcesos] = useState<Proceso[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTipo, setExpandedTipo] = useState<Record<string, boolean>>({
    PE: true,
    PO: true,
    PS: true,
  });
  const [expandedGroup, setExpandedGroup] = useState<Record<string, boolean>>({});
  const [pendientes, setPendientes] = useState<DocumentoResumen[]>([]);
  const [pendientesExpanded, setPendientesExpanded] = useState(true);

  useEffect(() => {
    fetchProcesos()
      .then((r) => setProcesos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchPendientes()
      .then((r) => setPendientes(r.datos ?? []))
      .catch(() => {});
  }, []);

  const enRevision = pendientes.filter((d) => d.estado === 'EN_REVISION');
  const enAprobacion = pendientes.filter((d) => d.estado === 'APROBADO');
  const totalPendientes = enRevision.length + enAprobacion.length;

  const grouped: GroupedProcess[] = useMemo(
    () =>
      (['PE', 'PO', 'PS'] as TipologiaProceso[]).map((tip) => {
        const tipProcs = procesos.filter((p) => p.tipologia === tip);
        const groupMap: Record<string, Proceso[]> = {};
        tipProcs.forEach((p) => {
          const g = p.grupoFuncional || 'Sin grupo';
          if (!groupMap[g]) groupMap[g] = [];
          groupMap[g].push(p);
        });
        return {
          tipologia: tip,
          groups: Object.entries(groupMap).map(([name, procs]) => ({
            name,
            procesos: procs.sort((a, b) => a.codigo.localeCompare(b.codigo)),
          })),
        };
      }),
    [procesos],
  );

  const toggleAll = useCallback(
    (expand: boolean) => {
      const tipo: Record<string, boolean> = {};
      const grp: Record<string, boolean> = {};
      (['PE', 'PO', 'PS'] as TipologiaProceso[]).forEach((t) => {
        tipo[t] = expand;
      });
      grouped.forEach((g) => {
        g.groups.forEach((gr) => {
          grp[`${g.tipologia}-${gr.name}`] = expand;
        });
      });
      setExpandedTipo(tipo);
      setExpandedGroup(grp);
    },
    [grouped],
  );

  return (
    <div
      style={{
        width: SIDEBAR_W,
        minWidth: SIDEBAR_W,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F8F9FB',
        color: C.text,
        fontFamily: 'inherit',
        overflow: 'hidden',
        borderRight: `1px solid ${C.border}`,
      }}
    >
      {/* Explorador de Documentos — link to go back */}
      <button
        onClick={onGoExplorer}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 18px',
          borderLeft: !selectedCodigo ? `3px solid ${C.azul2}` : '3px solid transparent',
          backgroundColor: !selectedCodigo ? '#EFF3FF' : 'transparent',
          color: C.text,
          fontSize: 13,
          fontWeight: 600,
          border: 'none',
          borderBottom: `1px solid ${C.border}`,
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          width: '100%',
          transition: 'background 0.15s',
        }}
      >
        <Ic d={ICON_PATHS.grid} s={16} c={C.azul2} />
        {t.workspace.exploradorDocumentos}
      </button>

      {/* MAPA DE PROCESOS label + expand/collapse */}
      <div
        style={{
          padding: '12px 18px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: C.textMut,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
          }}
        >
          {t.workspace.mapaProcesos}
        </span>
        <span style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => toggleAll(true)}
            style={{
              fontSize: 9,
              padding: '2px 6px',
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              background: 'none',
              color: C.textMut,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {'\u25BC'} Todo
          </button>
          <button
            onClick={() => toggleAll(false)}
            style={{
              fontSize: 9,
              padding: '2px 6px',
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              background: 'none',
              color: C.textMut,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {'\u25B6'} Todo
          </button>
        </span>
      </div>

      {/* Process tree */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '20px 18px', textAlign: 'center' }}>
            <LoadingSpinner text={t.workspace.cargandoProcesos} size="sm" />
          </div>
        ) : (
          grouped.map(({ tipologia, groups }) => {
            const tipCfg = TIPOLOGIA_CONFIG[tipologia];
            const isExpanded = expandedTipo[tipologia] ?? true;
            return (
              <div key={tipologia}>
                <button
                  onClick={() => setExpandedTipo((s) => ({ ...s, [tipologia]: !isExpanded }))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '8px 18px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      backgroundColor: tipCfg.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.text, flex: 1 }}>
                    {tipCfg.label}
                  </span>
                  <span style={{ fontSize: 10, color: C.textMut }}>
                    {isExpanded ? '\u25BC' : '\u25B6'}
                  </span>
                </button>

                {isExpanded &&
                  groups.map(({ name, procesos: procs }) => {
                    const groupKey = `${tipologia}-${name}`;
                    const isGroupExpanded = expandedGroup[groupKey] ?? false;
                    return (
                      <div key={groupKey}>
                        <button
                          onClick={() =>
                            setExpandedGroup((s) => ({ ...s, [groupKey]: !isGroupExpanded }))
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            width: '100%',
                            padding: '5px 18px 5px 30px',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                          }}
                        >
                          <span style={{ fontSize: 10, color: C.textMut }}>
                            {isGroupExpanded ? '\u25BC' : '\u25B6'}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 500, color: C.azul2 }}>
                            {name.replace(/^Gr\.\d+\s+/, '')}
                          </span>
                        </button>

                        {isGroupExpanded &&
                          procs.map((proc) => {
                            const active = selectedCodigo === proc.codigo;
                            return (
                              <button
                                key={proc.id}
                                onClick={() => onSelectProceso(proc)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  padding: active ? '4px 18px 4px 45px' : '4px 18px 4px 48px',
                                  borderLeft: active
                                    ? `3px solid ${C.azul2}`
                                    : '3px solid transparent',
                                  borderTop: 'none',
                                  borderRight: 'none',
                                  borderBottom: 'none',
                                  backgroundColor: active ? '#EFF3FF' : 'transparent',
                                  color: active ? C.azul1 : C.textSec,
                                  fontSize: 10,
                                  fontWeight: 400,
                                  transition: 'background 0.15s',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                  textAlign: 'left',
                                }}
                              >
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      marginRight: 6,
                                      fontSize: 10,
                                      color: active ? C.azul2 : C.text,
                                    }}
                                  >
                                    {proc.codigo}
                                  </span>
                                  <span style={{ opacity: 0.8 }}>{proc.nombre}</span>
                                </span>
                                {(proc._count?.documentos ?? 0) > 0 && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      padding: '1px 6px',
                                      borderRadius: 10,
                                      backgroundColor: '#E8ECF4',
                                      color: C.azul2,
                                      fontWeight: 600,
                                      flexShrink: 0,
                                      marginLeft: 6,
                                    }}
                                  >
                                    {proc._count!.documentos}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>

      {/* Pendientes section */}
      {totalPendientes > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderTop: `1px solid ${C.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setPendientesExpanded((e) => !e)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              width: '100%',
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#D97706',
                letterSpacing: 1.5,
                textTransform: 'uppercase',
              }}
            >
              {t.workspace.pendientes}
            </span>
            <span
              style={{
                fontSize: 9,
                padding: '1px 6px',
                borderRadius: 10,
                backgroundColor: '#FEF3C7',
                color: '#D97706',
                fontWeight: 700,
              }}
            >
              {totalPendientes}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 10, color: C.textMut }}>
              {pendientesExpanded ? '\u25BC' : '\u25B6'}
            </span>
          </button>

          {pendientesExpanded && (
            <>
              {enRevision.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#D97706',
                      padding: '2px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {t.workspace.enRevision}
                    <span
                      style={{
                        fontSize: 9,
                        padding: '0px 5px',
                        borderRadius: 8,
                        backgroundColor: '#FEF3C7',
                        color: '#D97706',
                        fontWeight: 700,
                      }}
                    >
                      {enRevision.length}
                    </span>
                  </div>
                  {enRevision.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => onNavigateDoc(d.codigo)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        padding: '4px 10px 4px 18px',
                        border: 'none',
                        borderRadius: 4,
                        backgroundColor: 'transparent',
                        color: C.textSec,
                        fontSize: 11,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 10,
                          color: '#D97706',
                          flexShrink: 0,
                          fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                        }}
                      >
                        {d.codigo}
                      </span>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {d.titulo}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {enAprobacion.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: C.azul2,
                      padding: '2px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {t.workspace.enAprobacion}
                    <span
                      style={{
                        fontSize: 9,
                        padding: '0px 5px',
                        borderRadius: 8,
                        backgroundColor: '#EFF3FF',
                        color: C.azul2,
                        fontWeight: 700,
                      }}
                    >
                      {enAprobacion.length}
                    </span>
                  </div>
                  {enAprobacion.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => onNavigateDoc(d.codigo)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        padding: '4px 10px 4px 18px',
                        border: 'none',
                        borderRadius: 4,
                        backgroundColor: 'transparent',
                        color: C.textSec,
                        fontSize: 11,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 10,
                          color: C.azul2,
                          flexShrink: 0,
                          fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                        }}
                      >
                        {d.codigo}
                      </span>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {d.titulo}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT — ExplorerWorkspace (no own header/nav)
// ═══════════════════════════════════════════════════════════════
export function ExplorerWorkspace({ embedded = false }: { embedded?: boolean }) {
  const { t } = useI18n();
  const navigate = useNavigate();

  // ─── State ───
  const [selectedProceso, setSelectedProceso] = useState<Proceso | null>(null);
  const [selectedDocumento, setSelectedDocumento] = useState<DocumentoResumen | null>(null);
  const [fichaEditing, setFichaEditing] = useState(false);
  const [docEditing, setDocEditing] = useState(false);
  const fichaRef = useRef<HTMLDivElement>(null);
  const docExportRef = useRef<(() => Promise<void>) | null>(null);

  // ─── Ficha display mode: 'full' | 'docs' | 'hidden' | 'flow' ───
  type FichaMode = 'full' | 'docs' | 'hidden' | 'flow';
  const [fichaMode, setFichaMode] = useState<FichaMode>('full');
  const fichaCollapsed = fichaMode === 'hidden';
  const cycleFichaMode = useCallback(() => {
    setFichaMode((m) => (m === 'full' ? 'docs' : m === 'docs' ? 'hidden' : 'full'));
  }, []);

  // ─── Panel state from DocumentPreviewPanel ───
  const [panelState, setPanelState] = useState<PanelState | null>(null);

  const handleExportFicha = useCallback(async () => {
    if (!fichaRef.current || !selectedProceso) return;
    await exportFichaToPdf(fichaRef.current, {
      codigo: `${selectedProceso.codigo}.FC.01`,
      nombre: `Ficha de Caracterizacion \u2014 ${selectedProceso.nombre}`,
      proceso: `${selectedProceso.codigo} ${selectedProceso.nombre}`,
    });
  }, [selectedProceso]);

  // ─── Handlers ───
  const handleSelectProceso = useCallback((p: Proceso) => {
    setSelectedProceso(p);
    setSelectedDocumento(null);
  }, []);

  const handleGoExplorer = useCallback(() => {
    setSelectedProceso(null);
    setSelectedDocumento(null);
  }, []);

  const handleSelectDoc = useCallback((doc: DocumentoResumen) => {
    setSelectedDocumento(doc);
    setDocEditing(false);
  }, []);

  // ─── Derived panel info for header ───
  const selectedVersion = panelState?.versions?.[panelState.selectedVersionIndex] ?? null;
  const estado = (panelState?.doc?.estado ??
    selectedDocumento?.estado ??
    'BORRADOR') as EstadoDocumento;
  const editionLabel = selectedVersion
    ? `Ed. ${String(selectedVersion.numeroEdicion).padStart(2, '0')}`
    : '';
  const transitions = panelState?.transitions ?? [];
  const handlers = panelState?.handlers ?? null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: C.pageBg,
        overflow: 'hidden',
        fontFamily: 'inherit',
      }}
    >
      {/* ═══ PRESENTATION HEADER (AppHeader) — hidden when embedded ═══ */}
      {!embedded && (
        <AppHeader
          modulo="Information"
          titulo={
            selectedProceso
              ? `${selectedProceso.codigo} — ${selectedProceso.nombre}`
              : 'Explorador de Documentos'
          }
          gradient="information"
          actions={
            selectedProceso ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {(['full', 'docs', 'hidden', 'flow'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setFichaMode(mode)}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: 8,
                      background: fichaMode === mode ? 'rgba(255,255,255,0.2)' : 'transparent',
                      color: fichaMode === mode ? '#fff' : 'rgba(255,255,255,0.6)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {mode === 'full'
                      ? t.workspace.ficha
                      : mode === 'docs'
                        ? t.workspace.docs
                        : mode === 'flow'
                          ? t.workspace.flow
                          : t.workspace.visor}
                  </button>
                ))}
                {selectedDocumento && (
                  <>
                    <div
                      style={{
                        width: 1,
                        height: 24,
                        background: 'rgba(255,255,255,0.2)',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#33BCE1',
                        fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selectedDocumento.codigo}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'rgba(255,255,255,0.85)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        maxWidth: 260,
                      }}
                    >
                      {selectedDocumento.titulo}
                    </span>
                    {editionLabel && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.7)',
                          padding: '3px 8px',
                          borderRadius: 4,
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {editionLabel}
                      </span>
                    )}
                  </>
                )}
              </div>
            ) : undefined
          }
        />
      )}

      {/* ═══ BODY: sidebar + content, below header ═══ */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* ═══ PROCESS TREE SIDEBAR ═══ */}
        <ProcessTreeSidebar
          selectedCodigo={selectedProceso?.codigo ?? null}
          onSelectProceso={handleSelectProceso}
          onGoExplorer={handleGoExplorer}
          onNavigateDoc={(codigo) => navigate(`/documento/${codigo}`)}
        />

        {/* ═══ CONTENT AREA ═══ */}
        {!selectedProceso ? (
          /* ─── No process → ExplorerPage global ─── */
          <div style={{ flex: 1, overflow: 'auto', padding: 24, backgroundColor: C.bg }}>
            <ExplorerPage />
          </div>
        ) : (
          /* ─── Process selected → sub-header + Ficha + Visor or Flow ─── */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {fichaMode === 'flow' ? (
              /* ─── Flow mode → full-height FlowEditor (editable) ─── */
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <FlowEditor procesoId={selectedProceso.id} />
              </div>
            ) : (
              <>
                {/* ═══ FUNCTIONAL SUB-HEADER (white, all controls) ═══ */}
                <div
                  style={{
                    height: 40,
                    minHeight: 40,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: C.surface,
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  {/* Left half — Ficha header */}
                  {fichaMode !== 'hidden' ? (
                    <div
                      style={{
                        flex: fichaMode === 'full' ? 5 : 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '0 16px',
                        borderRight: `1px solid ${C.border}`,
                        height: '100%',
                        minWidth: 0,
                        transition: 'flex 0.2s',
                      }}
                    >
                      <button
                        onClick={cycleFichaMode}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          color: C.textSec,
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                        title={
                          fichaMode === 'full'
                            ? t.workspace.tooltips.showOnlyDocs
                            : t.workspace.tooltips.hideSheet
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        >
                          <line x1="3" y1="6" x2="21" y2="6" />
                          <line x1="3" y1="12" x2="21" y2="12" />
                          <line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                      </button>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: C.text,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fichaMode === 'full'
                          ? t.workspace.fichaCaracterizacion
                          : t.workspace.documentos}
                      </span>
                      {fichaMode === 'full' && (
                        <>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: C.azul2,
                              fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {selectedProceso.codigo}.FC.01
                          </span>
                          <StatusBadge status="PUBLICADO" small />
                        </>
                      )}
                      <div style={{ flex: 1 }} />
                      {fichaMode === 'full' &&
                        (fichaEditing ? (
                          <>
                            <button onClick={() => setFichaEditing(false)} style={hdrBtn}>
                              <Ic d={ICON_PATHS.save} s={13} c={C.azul2} /> {t.common.guardar}
                            </button>
                            <button onClick={() => setFichaEditing(false)} style={hdrBtn}>
                              {t.common.cancelar}
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setFichaEditing(true)} style={hdrBtn}>
                              <Ic d={ICON_PATHS.edit} s={13} c={C.textSec} /> {t.common.editar}
                            </button>
                            <button onClick={handleExportFicha} style={hdrBtn}>
                              <Ic d={ICON_PATHS.download} s={13} c={C.textSec} /> PDF
                            </button>
                          </>
                        ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        width: FICHA_COLLAPSED_W,
                        minWidth: FICHA_COLLAPSED_W,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: `1px solid ${C.border}`,
                        height: '100%',
                      }}
                    >
                      <button
                        onClick={cycleFichaMode}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          color: C.textSec,
                          borderRadius: 4,
                        }}
                        title={t.workspace.tooltips.showFullSheet}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        >
                          <line x1="3" y1="6" x2="21" y2="6" />
                          <line x1="3" y1="12" x2="21" y2="12" />
                          <line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Right half — Document panel header */}
                  <div
                    style={{
                      flex: fichaCollapsed ? 1 : 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0 12px',
                      height: '100%',
                      minWidth: 0,
                    }}
                  >
                    {selectedDocumento ? (
                      <>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: C.azul2,
                            fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {selectedDocumento.codigo}
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            maxWidth: fichaCollapsed ? 300 : 180,
                          }}
                        >
                          {selectedDocumento.titulo}
                        </span>
                        <StatusBadge status={estado} small />
                        {editionLabel && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: C.textSec,
                              padding: '1px 5px',
                              borderRadius: 3,
                              backgroundColor: C.borderAlt,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {editionLabel}
                          </span>
                        )}
                        {panelState && panelState.versions.length > 1 && !docEditing && (
                          <select
                            value={panelState.selectedVersionIndex}
                            onChange={(e) => {
                              const idx = Number(e.target.value);
                              handlers?.selectVersion(idx);
                            }}
                            style={{
                              padding: '2px 4px',
                              fontSize: 10,
                              fontFamily: 'inherit',
                              border: `1px solid ${C.border}`,
                              borderRadius: 3,
                              backgroundColor: C.surface,
                              color: C.text,
                              maxWidth: 140,
                              cursor: 'pointer',
                            }}
                          >
                            {panelState.versions.map((v, i) => {
                              const vStatus = VERSION_STATUS_CONFIG[v.estado];
                              return (
                                <option key={v.id} value={i}>
                                  Ed. {String(v.numeroEdicion).padStart(2, '0')} (
                                  {v.id === (panelState?.doc as any)?.versionVigenteId
                                    ? t.status.PUBLICADO
                                    : ((t.versionStatus as Record<string, string>)[v.estado] ??
                                      vStatus?.label ??
                                      v.estado)}
                                  )
                                </option>
                              );
                            })}
                          </select>
                        )}
                        <div style={{ flex: 1 }} />
                        {!docEditing && transitions.length > 0 && (
                          <>
                            {transitions.map((tr) => (
                              <button
                                key={tr.accion}
                                onClick={() => handlers?.triggerTransition(tr.accion)}
                                disabled={panelState?.actionLoading}
                                style={{
                                  ...wfBtn,
                                  borderColor: tr.color,
                                  color: tr.color,
                                  opacity: panelState?.actionLoading ? 0.6 : 1,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = tr.color;
                                  e.currentTarget.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'white';
                                  e.currentTarget.style.color = tr.color;
                                }}
                              >
                                {panelState?.actionLoading ? '...' : tr.label}
                              </button>
                            ))}
                          </>
                        )}
                        {!docEditing && panelState?.canStartNewEdition && (
                          <button
                            onClick={() => handlers?.openNewEdition()}
                            disabled={panelState?.actionLoading}
                            style={{
                              ...wfBtn,
                              borderColor: C.azul2,
                              color: C.azul2,
                              opacity: panelState?.actionLoading ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = C.azul2;
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.color = C.azul2;
                            }}
                          >
                            {t.common.newEditionBtn}
                          </button>
                        )}
                        {docEditing ? (
                          <>
                            <button
                              onClick={() => handlers?.manualSave()}
                              disabled={panelState?.saving}
                              style={hdrBtnPrimary}
                            >
                              <Ic d={ICON_PATHS.save} s={13} c="#fff" />
                              {panelState?.saving ? t.common.guardando : t.common.guardar}
                            </button>
                            <button onClick={() => handlers?.discardChanges()} style={hdrBtn}>
                              {t.common.discard}
                            </button>
                            <button onClick={() => handlers?.exitEditMode()} style={hdrBtn}>
                              {t.common.closeEditor}
                            </button>
                          </>
                        ) : (
                          <>
                            {panelState?.canEdit && (
                              <button onClick={() => handlers?.enterEditMode()} style={hdrBtn}>
                                <Ic d={ICON_PATHS.edit} s={13} c={C.textSec} /> {t.common.editar}
                              </button>
                            )}
                          </>
                        )}
                        <button style={hdrBtn} onClick={() => handlers?.exportPdf()}>
                          <Ic d={ICON_PATHS.download} s={13} c={C.textSec} /> PDF
                        </button>
                        <button
                          style={hdrBtn}
                          onClick={cycleFichaMode}
                          title={
                            fichaMode === 'full'
                              ? t.workspace.tooltips.showOnlyDocs
                              : fichaMode === 'docs'
                                ? t.workspace.tooltips.expandDocument
                                : t.workspace.tooltips.showFullSheet
                          }
                        >
                          {fichaMode === 'hidden' ? '\u25C0' : '\u25B6'}
                        </button>
                        <button
                          style={hdrBtn}
                          onClick={() =>
                            window.open(`/documento/${selectedDocumento.codigo}`, '_blank')
                          }
                          title={t.workspace.tooltips.openFullViewer}
                        >
                          {'\u26F6'}
                        </button>
                      </>
                    ) : (
                      <span
                        style={{ fontSize: 13, color: C.textMut, fontStyle: 'italic', flex: 1 }}
                      >
                        {t.workspace.selectFromSheet}
                      </span>
                    )}
                  </div>
                </div>

                {/* ═══ PANELS ═══ */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                  {/* Section 1 — Ficha */}
                  <div
                    style={{
                      width: fichaMode === 'hidden' ? FICHA_COLLAPSED_W : undefined,
                      minWidth: fichaMode === 'hidden' ? FICHA_COLLAPSED_W : undefined,
                      flex: fichaMode === 'full' ? 5 : fichaMode === 'docs' ? 3 : undefined,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRight: `1px solid ${C.border}`,
                      overflow: 'hidden',
                      backgroundColor: C.bg,
                      transition: 'width 0.2s, min-width 0.2s, flex 0.2s',
                    }}
                  >
                    {fichaMode === 'hidden' ? (
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          paddingTop: 16,
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            writingMode: 'vertical-rl',
                            textOrientation: 'mixed',
                            fontSize: 11,
                            fontWeight: 600,
                            color: C.textMut,
                            letterSpacing: 0.5,
                            whiteSpace: 'nowrap',
                            transform: 'rotate(180deg)',
                          }}
                        >
                          {t.workspace.fichaCaracterizacion}
                        </span>
                      </div>
                    ) : fichaMode === 'docs' ? (
                      <div style={{ flex: 1, overflow: 'auto' }}>
                        <ProcessSheetPanel
                          proceso={selectedProceso}
                          onSelectDocumento={handleSelectDoc}
                          twoColumnLayout={false}
                          isEditing={false}
                          onToggleEdit={() => {}}
                          showOnlyDocs
                        />
                      </div>
                    ) : (
                      <div ref={fichaRef} style={{ flex: 1, overflow: 'auto' }}>
                        <ProcessSheetPanel
                          proceso={selectedProceso}
                          onSelectDocumento={handleSelectDoc}
                          twoColumnLayout
                          isEditing={fichaEditing}
                          onToggleEdit={() => setFichaEditing((e) => !e)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Section 2 — Document Viewer */}
                  <div
                    style={{
                      flex: fichaMode === 'hidden' ? 1 : fichaMode === 'docs' ? 7 : 6,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      backgroundColor: C.pageBg,
                      transition: 'flex 0.2s',
                    }}
                  >
                    <div style={{ flex: 1, overflow: 'auto' }}>
                      <DocumentPreviewPanel
                        documento={selectedDocumento}
                        onNavigateDoc={(codigo) => navigate(`/documento/${codigo}`)}
                        onExpandFullscreen={
                          selectedDocumento
                            ? () => navigate(`/documento/${selectedDocumento.codigo}`)
                            : undefined
                        }
                        externalHeader
                        editingFromParent={docEditing}
                        onEditingChange={setDocEditing}
                        onExportRef={docExportRef}
                        onStateChange={setPanelState}
                        useContentPages
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExplorerWorkspace;
